import React, { useState, useEffect, useRef, useMemo } from "react";
import BaseElement from "./BaseElement";
import { getCloudFunctionUrl } from "../../../config/firebaseFunctionUrl";
import { PhotoIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";
import ImageLoadingPlaceholder from "./ImageLoadingPlaceholder";
import ImageJobLoadingPlaceholder from "./ImageJobLoadingPlaceholder";
import { db, auth } from "../../../config/firebase";
import { collection, onSnapshot, query, where, doc, getDoc, updateDoc, FieldValue } from "firebase/firestore";
import { useBook } from "../../../contexts/BookContext";

// Last in-progress job id per element and failure badge dismissals; module-level
// so they survive the frequent remounts of this component
const _lastSeenJobByElement = new Map();
const _dismissedFailures = new Set();

// Test hook: module state outlives a render, so tests reset it between cases.
export const _resetImageElementModuleState = () => {
  _lastSeenJobByElement.clear();
  _dismissedFailures.clear();
};

// Per-spread recovery queues for sequential processing within each spread
const _recoveryQueues = {}; // { spreadId: Promise }
const _enqueueRecovery = (spreadId, fn) => {
  if (!_recoveryQueues[spreadId]) {
    _recoveryQueues[spreadId] = Promise.resolve();
  }
  _recoveryQueues[spreadId] = _recoveryQueues[spreadId].then(fn).catch(err => {
    console.error('[ImageElement] Recovery queue error for spread:', { spreadId, error: err.message });
  });
};

export default function ImageElement(props) {
  // Create a mutable ref object to store border properties
  const originalFirestoreValuesRef = useRef({
    hasBorder: props.hasBorder,
    borderColor: props.borderColor,
    borderWidth: props.borderWidth,
    borderStyle: props.borderStyle,
    borderRadius: props.borderRadius,
    borderOpacity: props.borderOpacity,
    visualStyle: props.visualStyle
  });
  
  // CRITICAL FIX: Update the ref when props change
  // This ensures we always have the latest Firestore values
  useEffect(() => {
    console.log(`[PROP UPDATE] Element ${props.id} received new props:`, {
      borderWidth: props.borderWidth,
      borderStyle: props.borderStyle,
      props,
    });
    
    // Update our ref with the new values
    originalFirestoreValuesRef.current = {
      hasBorder: props.hasBorder,
      borderColor: props.borderColor,
      borderWidth: props.borderWidth,  // THIS is how we get 18, "dashed", etc.
      borderStyle: props.borderStyle,
      borderRadius: props.borderRadius,
      borderOpacity: props.borderOpacity,
      visualStyle: props.visualStyle
    };
  }, [props.hasBorder, props.borderColor, props.borderWidth, props.borderStyle, props.borderRadius, props.borderOpacity, props.id]);
  
  // Destructure props but KEEP the original props object intact for direct access
  const {
    id,
    src,
    position,
    size,
    style,
    zIndex,
    imagePrompts, // New array structure
    imagePrompt, // Fallback for old structure
    images, // NEW - array of {url, promptIndex}
    visualStyle, // <-- ADD THIS
    orientation, // <-- ADD THIS for modal default
    imageGenerationStatus,
    imageUpdateJobId,
    lastGenerationFailed,
    lastGenerationError,
    isSelected,
    onSelect,
    onMove,
    onResize,
    onUpdate,
    onDelete,
    onDuplicate,
    onZIndexChange,
    containerRef,
    ensureOverlay,
    saveElements, // <-- NEW: Function to save the entire spread to Firestore
    isGenerating, // NEW: Button generating state from ImageElementControlPanel
    // CRITICAL FIX: Remove default parameter values that override Firestore data
    // These defaults should only be applied if the value is actually undefined
    hasBorder,
    borderColor,
    borderWidth,
    borderStyle,
    borderRadius,
    borderOpacity,
    element, // <-- IMPORTANT: Check if border properties are in this nested object
  } = props;
  

  
  // Simple flags to track where border properties are found
  const foundInProps = props.borderWidth !== undefined;
  const foundInElement = props.element && props.element.borderWidth !== undefined;
  
  // Store the correct source of border properties
  const borderSource = foundInElement ? 'element' : (foundInProps ? 'props' : 'default');
  const [imageError, setImageError] = useState(false);
  const [jobInfo, setJobInfo] = useState(null);
  const [jobElapsedMs, setJobElapsedMs] = useState(null);
  const [isCancellingJob, setIsCancellingJob] = useState(false);
  const [jobFailedLocally, setJobFailedLocally] = useState(false);
  const lastSeenJobIdRef = useRef(null);
  const [aspectRatio, setAspectRatio] = useState(null);
  const [detectedOrientation, setDetectedOrientation] = useState(null);
  const [previousOrientation, setPreviousOrientation] = useState(null);
  const [orientationLoaded, setOrientationLoaded] = useState(false);
  const { currentBook, loadBook, loadingUIMessage, showImageLoadingPlaceholder, processingStatus, pendingImageJobs, pendingImageJobsLoaded } = useBook();

  const activeJob = useMemo(() => {
    if (!pendingImageJobs || pendingImageJobs.size === 0) return null;
    if (imageUpdateJobId) {
      const byId = pendingImageJobs.get(imageUpdateJobId);
      if (byId) return byId;
    }
    for (const job of pendingImageJobs.values()) {
      if (job.elementId === id) return job;
    }
    return null;
  }, [pendingImageJobs, imageUpdateJobId, id]);

  const isJobPending = useMemo(() => {
    const terminal = ['succeeded', 'failed', 'no_image', 'completed', 'cancelled'];
    if (activeJob && !terminal.includes(activeJob.status)) return true;
    // Fallback: this element's own realtime job query may already see an
    // in-flight job while the book-level pendingImageJobs map is still catching
    // up (or missed the doc entirely).
    if (jobInfo && !terminal.includes(jobInfo.status)) return true;
    return false;
  }, [activeJob, jobInfo]);

  const waitingForJobCheck = !!imageUpdateJobId;

  const showImageContent = useMemo(() => {
    return !isJobPending && (!waitingForJobCheck || pendingImageJobsLoaded);
  }, [isJobPending, waitingForJobCheck, pendingImageJobsLoaded]);

  // Determine if we should show the global loading placeholder inside this image element
  const shouldShowGlobalLoadingPlaceholder = useMemo(() => {
    const processingActive = !!(processingStatus && processingStatus !== 'completed' && processingStatus !== 'error' && processingStatus !== 'cancelled');
    const bookNotCompleted = !!(currentBook && currentBook.status && currentBook.status !== 'completed');
    const globalLoadingActive = !!(showImageLoadingPlaceholder || processingActive || bookNotCompleted);

    // Don't show if this specific element has its own job loading
    return globalLoadingActive && !isJobPending;
  }, [processingStatus, currentBook, showImageLoadingPlaceholder, isJobPending]);

  // Get loading message for global placeholder
  const globalLoadingMessage = useMemo(() => {
    const statusMessages = {
      'pending':                    { progress: 5,   message: 'Please Wait...' },
      'pending2':                   { progress: 5,   message: 'Please Wait....' },
      'processing':                 { progress: 5,   message: 'Please Wait.....' },
      'savingTextElements':         { progress: 15,  message: 'Saving Text Elements...' },
      'updatingTextElements':       { progress: 16,  message: 'Updating Text Elements...' },
      'updatingTextElements2':      { progress: 17,  message: 'Updating Text Elements...' },
      'textElementsCreated':        { progress: 20,  message: 'Text Elements Created...' },
      'received':                   { progress: 30,  message: 'Request Received...' },
      'initializing':               { progress: 40,  message: 'Initializing image generation...' },
      'generatingCharacter':        { progress: 50,  message: 'Generating Character Details...' },
      'generatingImagePrompts':     { progress: 65,  message: 'Generating Image Prompts...' },
      'generatingCoverImage':       { progress: 80,  message: 'Generating Cover Image...' },
      'updatingImageElements':      { progress: 85,  message: 'Updating Image Elements...' },
      'updatedTextElementsInterior':{ progress: 85,  message: 'Finalizing Text Elements...' },
      'updatedCoverImage':          { progress: 90,  message: 'Finalizing Cover Image...' },
      'completed':                  { progress: 100, message: 'Complete!' },
    };
    if (processingStatus && statusMessages[processingStatus]) {
      return statusMessages[processingStatus];
    }
    return loadingUIMessage || { progress: 0, message: 'Processing...' };
  }, [processingStatus, loadingUIMessage]);

  useEffect(() => {
    const loadOrientationFromFirestore = async () => {
      if (!currentBook?.id) return;
      
      try {
        const spreadId = props.spreadId || "spread-0";
        const docRef = doc(db, 'books', currentBook.id, 'spreads', spreadId, 'elements', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const storedOrientation = data?.orientation;
          
          if (storedOrientation) {
            console.log("[ImageElement] Loaded orientation from Firestore:", {
              id,
              orientation: storedOrientation
            });
            setPreviousOrientation(storedOrientation);
          } else {
            // No stored orientation, use defaults
            const isCover = props.spreadId === 'spread-0';
            const defaultOrientation = isCover ? 'landscape' : 'portrait';
            console.log("[ImageElement] No stored orientation, using default:", {
              id,
              isCover,
              defaultOrientation
            });
            setPreviousOrientation(defaultOrientation);
          }
        }
      } catch (err) {
        console.warn("[ImageElement] Failed to load orientation from Firestore:", err);
        // Fallback to defaults
        const isCover = props.spreadId === 'spread-0';
        const defaultOrientation = isCover ? 'landscape' : 'portrait';
        setPreviousOrientation(defaultOrientation);
      } finally {
        // Mark orientation as loaded regardless of success/failure
        setOrientationLoaded(true);
      }
    };
    
    loadOrientationFromFirestore();
  }, [id, currentBook?.id, props.spreadId]);

  // Listen for real-time updates from Firestore (when webhook updates element)
  // This ensures the component re-renders immediately when the backend changes the element
  useEffect(() => {
    if (!currentBook?.id || !id) return;
    
    try {
      const spreadId = props.spreadId || "spread-0";
      const bookRef = doc(db, 'books', currentBook.id);
      
      // Subscribe to real-time updates of the book's nested spreads field
      // The backend writes to spreads.{spreadId}.elements which is where the actual data lives
      const unsubscribe = onSnapshot(bookRef, (bookSnap) => {
        if (bookSnap.exists()) {
          const bookData = bookSnap.data();
          const spreadData = bookData?.spreads?.[spreadId];
          const elements = Array.isArray(spreadData?.elements) ? spreadData.elements : [];
          const element = elements.find(el => (el.id || el.docId) === id);
          
          if (element) {
            const firestoreSrc = element?.src;
            const firestoreImages = element?.images;
            const firestoreOrientation = element?.orientation;
            const firestoreVisualStyle = element?.visualStyle;

            // Sync failure state in realtime: the backend tags the element with
            // lastGenerationFailed when a job fails, but props won't refresh until
            // a reload. This fires with current data on every (re)mount, so it also
            // survives component remounts. Skip if the user dismissed the badge.
            if (element?.lastGenerationFailed === true && !_dismissedFailures.has(id)) {
              setJobFailedLocally(true);
            }
            
            // Check if image data changed (src, images array, orientation, visualStyle)
            // NOTE: We intentionally do NOT sync size from Firestore - size is only updated through local resize
            const srcChanged = firestoreSrc && firestoreSrc !== src;
            const imagesChanged = firestoreImages && JSON.stringify(firestoreImages) !== JSON.stringify(images);
            const orientationChanged = firestoreOrientation && firestoreOrientation !== props.orientation;
            const visualStyleChanged = firestoreVisualStyle && firestoreVisualStyle !== props.visualStyle;
            
            if (srcChanged || imagesChanged || orientationChanged || visualStyleChanged) {
              console.log('[ImageElement] Detected updates from Firestore webhook:', {
                id,
                srcChanged: srcChanged ? { old: src?.substring(0, 60), new: firestoreSrc?.substring(0, 60) } : false,
                imagesChanged,
                orientationChanged,
                visualStyleChanged,
                canonicalDiagonal: element?.canonicalDiagonal
              });
              
              // Build update object with all changed fields
              const updateObj = {
                id,
                type: 'image'
              };
              if (srcChanged) {
                updateObj.src = firestoreSrc;
              }
              if (imagesChanged) {
                updateObj.images = firestoreImages;
              }
              if (orientationChanged) {
                updateObj.orientation = firestoreOrientation;
              }
              if (visualStyleChanged) {
                updateObj.visualStyle = firestoreVisualStyle;
              }
              if (element?.canonicalDiagonal) {
                updateObj.canonicalDiagonal = element.canonicalDiagonal;
              }
              
              // Notify parent to update the element
              onUpdate?.(updateObj);
            }
          }
        }
      }, (err) => {
        console.warn('[ImageElement] Error listening to element updates:', { elementId: id, error: err.message });
      });
      
      return () => unsubscribe();
    } catch (err) {
      console.warn('[ImageElement] Failed to set up element listener:', err);
    }
  }, [id, currentBook?.id, props.spreadId, src, images, props.orientation, props.visualStyle, onUpdate]);

  // Recover stale image element data when job has completed but element still shows pending
  const recoverStaleImageElement = async (jobId) => {
    if (!jobId || !currentBook?.id || !id) return;
    
    try {
      console.log('[ImageElement] Attempting to recover stale element:', { elementId: id, jobId });
      
      // Fetch the job document
      const jobDocRef = doc(db, 'imageUpdateJobs', jobId);
      const jobSnap = await getDoc(jobDocRef);
      
      if (!jobSnap.exists()) {
        console.log('[ImageElement] Job document not found, marking element as error:', { elementId: id, jobId });
        setJobFailedLocally(true);
        // Mark element as error since job doesn't exist
        if (props.spreadId && currentBook.id) {
          const spreadRef = doc(db, 'books', currentBook.id, 'spreads', props.spreadId);
          const spreadSnap = await getDoc(spreadRef);
          if (spreadSnap.exists()) {
            const spread = spreadSnap.data() || {};
            const elements = Array.isArray(spread.elements) ? spread.elements : [];
            const updatedElements = elements.map(el => {
              if ((el.id || el.docId) === id) {
                return {
                  ...el,
                  imageGenerationStatus: 'error',
                  lastGenerationFailed: true,
                  lastGenerationError: 'Job not found',
                  imageUpdateJobId: null
                };
              }
              return el;
            });
            await updateDoc(spreadRef, { elements: updatedElements });
          }
        }
        return;
      }
      
      const jobData = jobSnap.data();
      const jobStatus = jobData?.status;
      const jobCreatedAt = jobData?.createdAt?.toDate?.() || new Date(jobData?.createdAt);
      const jobAgeMs = Date.now() - jobCreatedAt.getTime();
      const threeMinutesMs = 3 * 60 * 1000;
      
      console.log('[ImageElement] Job found with status:', { elementId: id, jobId, jobStatus, jobAgeMs });
      
      // If job succeeded, update the element with the image data
      if (jobStatus === 'succeeded') {
        const receivedImageUrl = jobData?.receivedImageUrl;
        const orientation = jobData?.orientation;
        const position = jobData?.position;
        
        console.log('[ImageElement] Job succeeded, updating element with image data:', { elementId: id, hasImageUrl: !!receivedImageUrl });
        
        if (props.spreadId && currentBook.id) {
          const spreadRef = doc(db, 'books', currentBook.id, 'spreads', props.spreadId);
          const spreadSnap = await getDoc(spreadRef);
          if (spreadSnap.exists()) {
            const spread = spreadSnap.data() || {};
            const elements = Array.isArray(spread.elements) ? spread.elements : [];
            const updatedElements = elements.map(el => {
              if ((el.id || el.docId) === id) {
                const currentImages = Array.isArray(el.images) ? el.images : [];
                const newImage = receivedImageUrl ? { url: receivedImageUrl, promptIndex: jobData?.promptIndex || 0 } : null;
                
                const updates = {
                  ...el,
                  src: receivedImageUrl || el.src,
                  imageGenerationStatus: null,
                  imageUpdateJobId: null,
                  lastGenerationFailed: false,
                  lastGenerationError: null
                };
                
                // Add image to history if we have a URL
                if (newImage) {
                  updates.images = [...currentImages, newImage];
                }
                
                // Update orientation and size if provided
                if (orientation) {
                  updates.orientation = orientation;
                  if (orientation === 'landscape') {
                    updates.size = { width: 450, height: 338 };
                  } else if (orientation === 'portrait') {
                    updates.size = { width: 435, height: 579 };
                  }
                }
                
                return updates;
              }
              return el;
            });
            await updateDoc(spreadRef, { elements: updatedElements });
            
            // Also update the nested spreads field on the main book doc (like updateImageElement does)
            // Use field path syntax to update only this spread's elements, not overwrite other spreads
            const bookRef = doc(db, 'books', currentBook.id);
            const fieldPath = `spreads.${props.spreadId}.elements`;
            await updateDoc(bookRef, {
              [fieldPath]: updatedElements
            });
            
            console.log('[ImageElement] Successfully recovered stale element:', { elementId: id, jobId });
            
            // Reload book to reflect recovered element in UI
            if (currentBook?.id) {
              loadBook(currentBook.id);
            }
          }
        }
      } else if (jobStatus === 'error' || jobStatus === 'failed' || jobStatus === 'no_image') {
        // Job has failed - mark element as error immediately regardless of age
        console.log('[ImageElement] Job status is error, marking element as failed immediately:', { elementId: id, jobId, jobStatus, jobAgeMs });
        setJobFailedLocally(true);
        if (props.spreadId && currentBook.id) {
          const spreadRef = doc(db, 'books', currentBook.id, 'spreads', props.spreadId);
          const spreadSnap = await getDoc(spreadRef);
          if (spreadSnap.exists()) {
            const spread = spreadSnap.data() || {};
            const elements = Array.isArray(spread.elements) ? spread.elements : [];
            const updatedElements = elements.map(el => {
              if ((el.id || el.docId) === id) {
                return {
                  ...el,
                  imageGenerationStatus: null,
                  lastGenerationFailed: true,
                  lastGenerationError: jobData.lastError || `Job failed (${Math.round(jobAgeMs / 1000)}s old)`,
                  imageUpdateJobId: null
                };
              }
              return el;
            });
            await updateDoc(spreadRef, { elements: updatedElements });
            
            // Also update the nested spreads field on the main book doc
            // Use field path syntax to update only this spread's elements, not overwrite other spreads
            const bookRef = doc(db, 'books', currentBook.id);
            const fieldPath = `spreads.${props.spreadId}.elements`;
            await updateDoc(bookRef, {
              [fieldPath]: updatedElements
            });
          }
        }
      } else {
        // Job is still processing, don't error yet
        console.log('[ImageElement] Job still processing, will retry later:', { elementId: id, jobId, jobStatus, jobAgeMs });
      }
    } catch (err) {
      console.error('[ImageElement] Error recovering stale element:', { elementId: id, jobId, error: err.message });
    }
  };

  // Show loading UI based on imageUpdateJobs collection query
  // This is more reliable than relying on element attributes which may not be saved yet
  useEffect(() => {
    if (!id || !currentBook?.id) return;
    
    // Query imageUpdateJobs collection for any in-progress jobs for this element
    // Track all in-progress states: pending (initial), received (webhook received), retrying (fallback attempt)
    const jobsRef = collection(db, 'imageUpdateJobs');
    const q = query(
      jobsRef,
      where('elementId', '==', id),
      where('bookId', '==', currentBook.id),
      where('status', 'in', ['pending', 'received', 'retrying'])
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (snapshot.empty) {
        console.log('[ImageElement] No pending jobs found for element:', { elementId: id, hasImageUpdateJobId: !!props.imageUpdateJobId });
        setJobInfo(null);

        // If we previously saw an in-progress job for this element, check its final
        // status so the failure UI shows immediately (the backend clears
        // imageUpdateJobId on failure, so the stale-recovery path below won't run).
        const lastSeenJobId = lastSeenJobIdRef.current || _lastSeenJobByElement.get(id);
        if (lastSeenJobId) {
          lastSeenJobIdRef.current = null;
          _lastSeenJobByElement.delete(id);
          getDoc(doc(db, 'imageUpdateJobs', lastSeenJobId))
            .then((jobSnap) => {
              const finalStatus = jobSnap.exists() ? jobSnap.data()?.status : null;
              console.log('[ImageElement] Last seen job finished with status:', { elementId: id, jobId: lastSeenJobId, finalStatus });
              if (finalStatus === 'error' || finalStatus === 'failed' || finalStatus === 'no_image') {
                setJobFailedLocally(true);
              }
            })
            .catch((err) => {
              console.warn('[ImageElement] Failed to check final job status:', { elementId: id, jobId: lastSeenJobId, error: err?.message });
            });
        }
        
        // Check if element has a stale imageUpdateJobId (job completed but element still shows pending)
        if (props.imageUpdateJobId) {
          console.log('[ImageElement] Queuing stale data recovery:', { elementId: id, jobId: props.imageUpdateJobId, spreadId: props.spreadId });
          _enqueueRecovery(props.spreadId, () => recoverStaleImageElement(props.imageUpdateJobId));
        } else if ((props.imageGenerationStatus === 'pending' || props.imageStatus === 'pending') && !props.src) {
          console.log('[ImageElement] Found orphaned pending element with no job ID and no image:', { elementId: id, hasSrc: !!props.src });
          if (props.spreadId && currentBook?.id) {
            (async () => {
              try {
                const spreadRef = doc(db, 'books', currentBook.id, 'spreads', props.spreadId);
                const spreadSnap = await getDoc(spreadRef);
                if (spreadSnap.exists()) {
                  const spread = spreadSnap.data() || {};
                  const elements = Array.isArray(spread.elements) ? spread.elements : [];
                  const updatedElements = elements.map(el => {
                    if ((el.id || el.docId) === id) {
                      return {
                        ...el,
                        imageGenerationStatus: null,
                        imageStatus: null,
                        lastGenerationFailed: true,
                        lastGenerationError: 'Job was lost or never created'
                      };
                    }
                    return el;
                  });
                  await updateDoc(spreadRef, { elements: updatedElements });
                  const bookRef = doc(db, 'books', currentBook.id);
                  const fieldPath = `spreads.${props.spreadId}.elements`;
                  await updateDoc(bookRef, { [fieldPath]: updatedElements });
                }
              } catch (err) {
                console.error('[ImageElement] Error clearing orphaned pending:', { elementId: id, error: err.message });
              }
            })();
          }
        }
        return;
      }
      
      // Get the most recent pending job
      const jobDoc = snapshot.docs[0];
      const jobData = jobDoc.data();
      const createdAtMs = jobData?.createdAt?.toMillis?.() || jobData?.createdAt;
      
      console.log('[ImageElement] Found pending job:', { jobId: jobDoc.id, elementId: id, createdAt: createdAtMs });
      
      lastSeenJobIdRef.current = jobDoc.id;
      _lastSeenJobByElement.set(id, jobDoc.id);
      _dismissedFailures.delete(id);
      setJobFailedLocally(false);
      setJobInfo({
        id: jobDoc.id,
        status: 'pending',
        message: 'Generating image...',
        ts: createdAtMs,
        lastError: null,
        fluxError: null
      });
    }, (err) => {
      console.error('[ImageElement] Error querying jobs:', { elementId: id, error: err.message });
    });
    
    return () => unsubscribe();
  }, [id, currentBook?.id, props.imageUpdateJobId]);

  // Elapsed timer for in-progress jobs
  useEffect(() => {
    const terminal = ['succeeded', 'failed', 'no_image', 'cancelled'];
    const isInProgress = !!(
      (jobInfo && jobInfo.status && !terminal.includes(jobInfo.status)) ||
      (activeJob && activeJob.status && !terminal.includes(activeJob.status))
    );

    // Prefer the per-element query timestamp; fall back to the active job's
    // createdAt so the timer starts even before jobInfo arrives
    let jobTs = null;
    if (typeof jobInfo?.ts === 'number' && Number.isFinite(jobInfo.ts) && jobInfo.ts > 0) {
      jobTs = jobInfo.ts;
    } else {
      const c = activeJob?.createdAt;
      if (typeof c === 'number' && c > 0) jobTs = c;
      else if (c?.toMillis) jobTs = c.toMillis();
      else if (c instanceof Date) jobTs = c.getTime();
      else if (c?.seconds) jobTs = c.seconds * 1000;
    }

    if (!isInProgress || !jobTs) {
      setJobElapsedMs(null);
      return undefined;
    }

    const tick = () => setJobElapsedMs(Date.now() - jobTs);
    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [jobInfo?.ts, jobInfo?.status, activeJob?.createdAt, activeJob?.status]);

  const handleCancelJob = async () => {
    const jobId = activeJob?.id || jobInfo?.id;
    if (!jobId) return;
    try {
      setIsCancellingJob(true);
      const user = auth?.currentUser;
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const resp = await fetch(
        getCloudFunctionUrl('cancelImageUpdateJob'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ jobId }),
        }
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.success) {
        throw new Error(json?.error || 'Failed to cancel');
      }

      // Clear pending state on the element so the UI stops showing "Generating..."
      onUpdate?.({
        id,
        type: 'image',
        imageUpdateJobId: null,
        imageGenerationStatus: null,
      });
      setJobInfo(null);
      setJobElapsedMs(null);
    } catch (e) {
      try { console.warn('[ImageElement] cancel job failed', e?.message || e); } catch (_) {}
    } finally {
      setIsCancellingJob(false);
    }
  };
  
  // IMPROVED: Initialize border properties from props (backend sets defaults when image is generated)
  const [borderProperties, setBorderProperties] = useState({
    hasBorder: hasBorder !== undefined ? hasBorder : true,
    borderColor: borderColor || "#ffffff",
    borderWidth: borderWidth || 8,
    borderStyle: borderStyle || "solid",
    borderRadius: borderRadius || 8,
    borderOpacity: borderOpacity !== undefined ? borderOpacity : 100
  });
  
  // Use a ref to store the original border properties that we want to maintain
  const originalBorderRef = useRef(null);
  
  // Track whether this component has initialized border properties from Firebase
  const borderPropertiesInitialized = useRef(false);
  
  // This will help us know when we've manually set border properties that should be preserved
  const [borderLocked, setBorderLocked] = useState(false);
  
  // Update borderProperties state when props change (e.g., after reload)
  useEffect(() => {
    // URGENT FIX: Log the borderStyle prop again inside the effect
    console.log(`[CRITICAL FIX] BorderStyle in effect for image ${id}:`, {
      borderStyle,
      borderStyleType: typeof borderStyle,
      propSource: 'From props passed to component',
      customValue: 'Should be "dashed" from Firestore'
    });
    
    // CRITICAL Fix: Force unlock border properties on initial load to ensure Firebase values are used
    // This ensures values from the database take precedence on component mount
    if (!borderPropertiesInitialized.current) {
      console.log("[BorderDebug] FIRST LOAD - Using props from Firebase for element:", id);
      setBorderLocked(false);
      borderPropertiesInitialized.current = true;
    }
    
    // Only use locked properties during the same editing session, but not after reload
    if (borderLocked && originalBorderRef.current) {
      console.log("[BorderDebug] Using locked border properties instead of props:", originalBorderRef.current);
      // Use our locked border properties to override any incoming props
      setBorderProperties(originalBorderRef.current);
      return;
    }
    
    console.log("[BorderDebug] RAW PROPS received by ImageElement:", {
      hasBorder,
      borderColor: borderColor, 
      borderColorType: typeof borderColor,
      borderWidth: borderWidth, 
      borderWidthType: typeof borderWidth,
      borderStyle: borderStyle,
      borderStyleType: typeof borderStyle, 
      borderRadius: borderRadius,
      borderRadiusType: typeof borderRadius,
      borderOpacity: borderOpacity,
      borderOpacityType: typeof borderOpacity
    });
    
    // CRITICAL: Log raw values first before any processing
    console.log(`[CRITICAL DATA] RAW border values for ${id} BEFORE processing:`, {
      hasBorder,
      hasBorderType: typeof hasBorder,
      borderColor,
      borderColorType: typeof borderColor,
      borderWidth,
      borderWidthType: typeof borderWidth,
      borderStyle, 
      borderStyleType: typeof borderStyle,
      borderRadius,
      borderRadiusType: typeof borderRadius,
      borderOpacity,
      borderOpacityType: typeof borderOpacity,
    });
    
    // Fix potential serialization issues by ensuring properties are the right type
    // but ONLY apply defaults when the value is truly undefined/null
    const processedBorderProps = {
      hasBorder: hasBorder !== undefined ? Boolean(hasBorder) : true,
      borderColor: borderColor || '#ffffff',
      borderWidth: borderWidth || 8,
      borderStyle: borderStyle || 'solid',
      borderRadius: borderRadius || 8,
      borderOpacity: borderOpacity !== undefined ? Number(borderOpacity) : 100
    };
    
    setBorderProperties(processedBorderProps);
    
    console.log("[BorderDebug] Processed border props after type conversion:", processedBorderProps);
  }, [hasBorder, borderColor, borderWidth, borderStyle, borderRadius, borderOpacity, borderLocked]);

  // Debug props when they change
  useEffect(() => {
    console.log("[ImageElement] Props updated:", {
      id,
      src,
      size,
      imagePrompts: imagePrompts,
      promptCount: imagePrompts?.length || 0,
      imagePrompt, // Legacy fallback
      visualStyle, // Log the visualStyle prop
      orientation, // Log the orientation prop
      hasPrompt: !!(imagePrompts && imagePrompts.length > 0) || !!imagePrompt,
    });
    // Reset error state when src changes
    setImageError(false);
  }, [id, src, size, imagePrompts, imagePrompt, orientation]);

  // Editing moved to ImageElementControlPanel (2026-05-21). Clicking an image only
  // selects it via BaseElement; this used to open a modal whose state no longer exists.
  const handleUpdate = () => {};

  const handleImageError = () => {
    console.log("[ImageElement] Image failed to load:", { id, src });
    setImageError(true);
  };


  const handleImageLoad = (e) => {
    const img = e.target;
    if (!img || !img.naturalWidth || !img.naturalHeight) {
      console.log("[ImageElement] Could not detect image dimensions");
      return;
    }

    const imgWidth = img.naturalWidth;
    const imgHeight = img.naturalHeight;
    const ratio = imgWidth / imgHeight;

    // Determine orientation
    const isLandscape = imgWidth > imgHeight;
    const orientation = isLandscape ? 'landscape' : 'portrait';

    // Store aspect ratio for resize constraint
    setAspectRatio(ratio);
    setDetectedOrientation(orientation);

    // Auto-resizing on image load is disabled
    // Size adjustments based on orientation changes are now handled by the backend (updateImageElement function)
    console.log("[ImageElement] Image loaded - orientation detected:", {
      id,
      orientation,
      ratio,
      currentSize: size
    });

    // Send orientation to backend via onUpdate so it can be persisted and used for resizing
    // This allows the backend to apply size adjustments based on orientation changes
    if (orientation !== previousOrientation) {
      console.log("[ImageElement] Orientation changed - notifying backend:", {
        id,
        from: previousOrientation,
        to: orientation
      });
      
      onUpdate?.({
        id,
        type: "image",
        orientation: orientation
      });
    }

    // Update previous orientation for next load
    setPreviousOrientation(orientation);
  };

  return (
    <>
      <BaseElement
        id={id}
        type="image"
        position={position}
        size={size}
        zIndex={zIndex}
        isSelected={isSelected}
        onSelect={onSelect}
        onMove={onMove}
        onResize={onResize}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        onUpdate={handleUpdate}
        onZIndexChange={onZIndexChange}
        containerRef={containerRef}
        aspectRatio={null}
      >
        <div
          className="w-full h-full relative group cursor-pointer"
          onClick={handleUpdate}
          data-element-id={id}
        >
          {/* Per-element ensureDesignPrompts overlay */}
          {ensureOverlay?.active && (
            <div className="absolute inset-0 z-50 pointer-events-none">
              <div className="relative" style={{ width: '100%', height: '100%' }}>
                <ImageLoadingPlaceholder
                  message={ensureOverlay.message}
                  progress={ensureOverlay.progress}
                  size={{ width: size?.width || 500, height: size?.height || 667 }}
                  position={{ x: 0, y: 0 }}
                  customTitle={ensureOverlay.customTitle}
                  customDescription={ensureOverlay.customDescription}
                  showSteps={ensureOverlay.showSteps === false ? false : true}
                />
              </div>
            </div>
          )}
          {showImageContent ? (
            <div className="w-full h-full relative">
              {src ? (
            <>
              {imageError && !(jobInfo && jobInfo.status && jobInfo.status !== 'succeeded' && jobInfo.status !== 'failed' && jobInfo.status !== 'no_image') ? (
                // Broken image state - but only show if no active job
                <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 rounded-lg border-2 border-dashed border-red-300">
                  <ExclamationCircleIcon className="h-12 w-12 text-red-400 mb-2" />
                  <p className="text-sm text-red-600 text-center px-4">
                    Failed to load image
                  </p>
                  <button className="mt-2 px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:underline">
                    Try generating another?
                  </button>
                </div>
              ) : (
                <>
                   <div className="relative w-full h-full p-0 m-0 flex items-center justify-center overflow-hidden">
                     {/* Simple centering wrapper - border is now applied directly to the image */}
                     <div 
                       className="flex items-center justify-center w-full h-full"
                       style={{
                        // No border styles here - just centering
                      }}
                    >
                      <img
                        key={src || id}
                        src={typeof src === 'string' ? src.trim() : src}
                        alt=""
                        className="w-auto h-auto max-w-full max-h-full"
                        style={{
                          ...style,
                          objectFit: 'contain',
                          objectPosition: 'center',
                          display: 'block',
                          margin: 0,
                          padding: 0,
                          // Apply border styles directly to the image
                          ...(() => {
                            const hasBorder = (borderLocked && originalBorderRef.current?.hasBorder) || borderProperties.hasBorder;
                            if (!hasBorder) {
                              return { borderRadius: '0px' };
                            }
                            
                            // Determine values to use (locked or current)
                            const borderColor = (borderLocked && originalBorderRef.current) 
                              ? originalBorderRef.current.borderColor 
                              : (borderProperties.borderColor || '#ffffff');
                              
                            const borderWidthValue = (borderLocked && originalBorderRef.current) 
                              ? originalBorderRef.current.borderWidth 
                              : borderProperties.borderWidth;
                              
                            const borderRadius = (borderLocked && originalBorderRef.current) 
                              ? originalBorderRef.current.borderRadius 
                              : borderProperties.borderRadius;
                              
                            const borderStyle = (borderLocked && originalBorderRef.current) 
                              ? originalBorderRef.current.borderStyle 
                              : (borderProperties.borderStyle || 'solid');
                            
                            // Apply border directly to image
                            return {
                              borderRadius: `${borderRadius}px`,
                              borderWidth: `${borderWidthValue}px`,
                              borderStyle: borderStyle,
                              borderColor: borderColor,
                              boxSizing: 'border-box'
                            };
                          })()
                        }}
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                        onError={handleImageError}
                        onLoad={handleImageLoad}
                      />
                    </div>
                    
                    {/* Apply border styling directly to the image */}
                    
                    {/* Blur overlay when button is generating */}
                    {isGenerating && (
                      <div className="absolute inset-0 backdrop-blur-md bg-white/40 rounded-lg z-20" />
                    )}
                  </div>
                  {/* Small warning icon when last regeneration failed but image still exists */}
                  {(lastGenerationFailed || jobFailedLocally) && (
                    <div 
                      className="absolute top-1 left-1 z-30 group/failbadge"
                      onMouseLeave={() => {
                        // Locally modify lastGenerationFailed to false
                        // This triggers "Save changes" button and hides the icon
                        _dismissedFailures.add(id);
                        setJobFailedLocally(false);
                        if (onUpdate) {
                          onUpdate({
                            ...props,
                            lastGenerationFailed: false
                          });
                        }
                      }}
                    >
                      <div className="w-7 h-7 rounded-full bg-amber-400 flex items-center justify-center shadow-sm cursor-help">
                        <span className="text-white text-sm font-bold leading-none">!</span>
                      </div>
                      <div className="absolute top-full left-0 mt-1 hidden group-hover/failbadge:block w-48 px-2 py-1.5 bg-gray-900 text-white text-[11px] rounded shadow-lg leading-tight pointer-events-none">
                        Last generation for this element failed. Credits were refunded.
                      </div>
                    </div>
                  )}
                  {/* Global loading placeholder overlay for existing images */}
                  {shouldShowGlobalLoadingPlaceholder && (
                    <div className="absolute inset-0 z-40 pointer-events-none">
                      <ImageLoadingPlaceholder 
                        message={globalLoadingMessage?.message || "Processing..."}
                        progress={globalLoadingMessage?.progress || 0}
                        size={{ width: size?.width || 500, height: size?.height || 667 }}
                        position={{ x: 0, y: 0 }}
                        showSteps={true}
                      />
                    </div>
                  )}
                  
                  {/* Pending job overlay even when image already exists */}
                  {isJobPending && (
                    <div className="absolute inset-0 z-50 pointer-events-auto">
                      <ImageJobLoadingPlaceholder
                        status={activeJob?.status || 'pending'}
                        message={activeJob?.message ?? jobInfo?.message}
                        progress={activeJob?.progress}
                        elapsedMs={jobElapsedMs}
                        onCancel={handleCancelJob}
                        isCancelling={isCancellingJob}
                        size={{ width: size?.width || 500, height: size?.height || 667 }}
                        position={{ x: 0, y: 0 }}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {isJobPending ? (
                <div className="w-full h-full relative">
                  <ImageJobLoadingPlaceholder
                    status={activeJob?.status || 'pending'}
                    message={activeJob?.message ?? jobInfo?.message}
                    progress={activeJob?.progress}
                    elapsedMs={jobElapsedMs}
                    onCancel={handleCancelJob}
                    isCancelling={isCancellingJob}
                    size={{ width: size?.width || 500, height: size?.height || 667 }}
                    position={{ x: 0, y: 0 }}
                  />
                </div>
              ) : false ? (
                <div className="w-full h-full relative">
                  <ImageJobLoadingPlaceholder
                    status={imageGenerationStatus || "pending"}
                    message={null}
                    progress={undefined}
                    elapsedMs={jobElapsedMs}
                    onCancel={handleCancelJob}
                    isCancelling={isCancellingJob}
                    size={{ width: size?.width || 500, height: size?.height || 667 }}
                    position={{ x: 0, y: 0 }}
                  />
                </div>
              ) : (
                <div className={`w-full h-full flex items-center justify-center rounded-lg transition-colors duration-200 ${(lastGenerationFailed || jobFailedLocally) ? 'bg-amber-50 border-2 border-dashed border-amber-400' : 'bg-gray-50 border-2 border-dashed border-gray-300 group-hover:border-indigo-300 group-hover:bg-gray-100'} relative`}>
                  {/* Blur overlay when button is generating */}
                  {isGenerating && (
                    <div className="absolute inset-0 backdrop-blur-md bg-white/40 rounded-lg z-20" />
                  )}
                  {/* Global loading placeholder overlay for empty image elements */}
                  {shouldShowGlobalLoadingPlaceholder && (
                    <div className="absolute inset-0 z-40 pointer-events-none">
                      <ImageLoadingPlaceholder 
                        message={globalLoadingMessage?.message || "Processing..."}
                        progress={globalLoadingMessage?.progress || 0}
                        size={{ width: size?.width || 500, height: size?.height || 667 }}
                        position={{ x: 0, y: 0 }}
                        showSteps={true}
                      />
                    </div>
                  )}
                  
                  <div className="text-center p-6 transform group-hover:scale-105 transition-transform duration-200">
                    {(lastGenerationFailed || jobFailedLocally) && (
                      <div className="mb-3 px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-md">
                        <p className="text-xs font-bold text-amber-700">Previous generation failed</p>
                        <p className="text-xs font-medium text-amber-700 mt-2">Credits refunded</p>
                      </div>
                    )}
                    <PhotoIcon className={`mx-auto h-12 w-12 transition-colors duration-200 ${(lastGenerationFailed || jobFailedLocally) ? 'text-amber-400' : 'text-gray-400 group-hover:text-indigo-400'}`} />
                    
                    <div className="mt-2 text-xs text-gray-500 group-hover:text-gray-600 transition-colors duration-200 space-y-1">
                      {imagePrompt ? (
                        <p>Click to edit image</p>
                      ) : (
                        <div className="text-left space-y-1">
                          <p className="font-medium">To generate an image:</p>
                          <ol className="list-decimal list-inside space-y-1 pl-1">
                            <li>Select this image element</li>
                            <li>Use the Control Panel found on the right</li>
                            <li>Add or Generate an Image Prompt</li>
                            <li>Choose Visual Style</li>
                            <li>Choose Orientation</li>
                            <li>Click Generate Image</li>
                          </ol>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
            </div>) : isJobPending ? (
            <div className="w-full h-full relative">
              <ImageJobLoadingPlaceholder
                status={activeJob?.status || 'pending'}
                message={activeJob?.message ?? jobInfo?.message}
                progress={activeJob?.progress}
                elapsedMs={jobElapsedMs}
                onCancel={handleCancelJob}
                isCancelling={isCancellingJob}
                size={{ width: size?.width || 500, height: size?.height || 667 }}
                position={{ x: 0, y: 0 }}
              />
            </div>
          ) : (
            <div className="w-full h-full" />
          )}
        </div>
      </BaseElement>

    </>
  );
}
